import React, { forwardRef, useEffect } from "react";
import { Monitor, Smartphone, Tablet } from "lucide-react";

const frameStyles = {
  desktop: "w-full h-full rounded-xl border border-gray-200",
  tablet:
    "w-[768px] max-h-full rounded-[1.25rem] border-[6px] border-gray-800 shadow-2xl",
  mobile:
    "w-[390px] max-h-full rounded-[1.75rem] border-[8px] border-gray-900 shadow-2xl",
};

const DeviceFrame = forwardRef(
  ({ mode = "desktop", children, onModeChange, scrollToTopToken = 0 }, ref) => {
    useEffect(() => {
      if (ref?.current) {
        ref.current.scrollTop = 0;
      }
    }, [scrollToTopToken, mode, ref]);

    const modes = [
      { id: "desktop", label: "Desktop", icon: Monitor },
      { id: "tablet", label: "Tablet", icon: Tablet },
      { id: "mobile", label: "Mobile", icon: Smartphone },
    ];

    return (
      <div className="flex flex-col h-full">
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 bg-white/80 backdrop-blur-sm rounded-t-2xl shrink-0">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex w-2 h-2 rounded-full bg-rose-400" />
            <span className="inline-flex w-2 h-2 rounded-full bg-amber-400" />
            <span className="inline-flex w-2 h-2 rounded-full bg-emerald-400" />
            <span className="ml-2 text-[11px] font-medium text-gray-500">Consumer preview</span>
          </div>

          <div className="inline-flex p-0.5 bg-gray-100 rounded-lg">
            {modes.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => onModeChange?.(id)}
                className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md transition-colors ${
                  mode === id
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="w-3 h-3" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-1 min-h-0 items-stretch justify-center p-2 sm:p-3 overflow-hidden bg-gradient-to-br from-slate-100 via-white to-rose-50 rounded-b-2xl">
          <div
            className={`relative flex flex-col overflow-hidden bg-white transition-all duration-500 ${frameStyles[mode] || frameStyles.desktop}`}
          >
            {mode === "mobile" ? (
              <div className="absolute top-0 left-1/2 z-20 w-24 h-4 -translate-x-1/2 bg-gray-900 rounded-b-xl" />
            ) : null}
            {mode === "tablet" ? (
              <div className="absolute top-0 left-1/2 z-20 w-2 h-2 -translate-x-1/2 bg-gray-700 rounded-full mt-1.5" />
            ) : null}
            <div
              ref={ref}
              className={`flex-1 min-h-0 overflow-y-auto ${
                mode === "mobile" ? "pt-2" : mode === "tablet" ? "pt-3" : ""
              }`}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

DeviceFrame.displayName = "DeviceFrame";

export default DeviceFrame;
